import { Button, Text, VStack} from '@chakra-ui/react';
import { useAudio } from '../contexts/AudioContext';
import { formatTime } from '../utils/audioUtils';

interface TimeRangeButtonProps {
  start: number;
  end: number;
  description?: string;
  'data-testid'?: string;
}

export const TimeRangeButton = ({ 
  start, 
  end, 
  description,
  'data-testid': testId 
}: TimeRangeButtonProps) => {
  const { audioElement, isPlaying, togglePlayPause, selectedTimeRange, setSelectedTimeRange } = useAudio();

  // Check if this specific time range is currently selected
  const isSelected = selectedTimeRange && 
    selectedTimeRange.start === start && 
    selectedTimeRange.end === end;

  // Check if this is the currently playing section (near start position)
  const isCurrentlyPlaying = () => {
    if (!audioElement || !isPlaying) return false;
    const currentTime = audioElement.currentTime;
    // Consider it "playing this section" if we're within 5 seconds of the start
    return Math.abs(currentTime - start) < 5;
  };

  const handleClick = () => {
    if (!audioElement) return;
    
    // Set this as the selected time range
    setSelectedTimeRange({ start, end, description });
    
    // Jump to the specific timestamp
    audioElement.currentTime = start;
    
    // Start playing if not already playing
    if (!isPlaying) {
      togglePlayPause();
    }
  };

  // Determine if this button should be highlighted (either selected or currently playing)
  const isHighlighted = isSelected || isCurrentlyPlaying();

  return (
    <Button
      onClick={handleClick}
      variant={isHighlighted ? "solid" : "outline"}
      colorScheme={isHighlighted ? "blue" : "gray"}
      size="sm"
      width="100%"
      height="auto"
      justifyContent="flex-start"
      py={4}
      px={4}
      transition="all 0.2s"
      whiteSpace="normal"
      textAlign="left"
      _hover={{
        transform: "translateY(-2px)",
        shadow: "md"
      }}
      data-testid={testId}
    >
      <VStack align="start" spacing={1} width="100%">
        <Text 
          fontSize="xs" 
          color={isHighlighted ? "white" : "gray.500"}
          width="100%"
        >
          {formatTime(start)}
          {end > start && ` - ${formatTime(end)}`}
        </Text>
        {description && (
          <Text 
            fontSize="sm" 
            color={isHighlighted ? "white" : "gray.700"} 
            width="100%"
            noOfLines={3}
            wordBreak="break-word"
          >
            {description}
          </Text>
        )}
      </VStack>
    </Button>
  );
}; 